import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import { useTheme } from "@mui/material/styles";
import { Card } from "@/components/molecules/Card";
import { ProductCard } from "../ProductCard/ProductCard";
import type { CategorySectionProps } from "./CategorySection.types";

/**
 * Category Section Component
 */
export const CategorySection: React.FC<CategorySectionProps> = React.memo(
  ({ category, products, loading = false }) => {
    const theme = useTheme();

    if (products.length === 0 && !loading) {
      return null;
    }

    return (
      <Box
        px={{
          xs: 2,
          x2l: 3,
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: 26,
            fontWeight: "bold",
            lineHeight: 1.1,
            pt: 3,
            pb: 2,
          }}
        >
          {category.name}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(1, 1fr)",
              lg: "repeat(2, 1fr)",
              sm: "repeat(2, 1fr)",
              l: "repeat(2, 1fr)",
              xl: "repeat(3, 1fr)",
              x2l: "repeat(4, 1fr)",
              x3l: "repeat(5, 1fr)",
              x4l: "repeat(6, 1fr)",
              x5l: "repeat(7, 1fr)",
              x6l: "repeat(8, 1fr)",
              x7l: "repeat(9, 1fr)",
            },
            gap: {
              xs: 1.5, // 12px
              sm: 1.75, // 14px
              md: 2, // 16px
              lg: 2, // 16px
              xl: 2.25, // 18px
            },
          }}
        >
          {loading
            ? Array.from({ length: 4 }).map((_, index) => (
                <Card key={index} sx={{ height: "100%" }}>
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      width: "100%",
                      aspectRatio: "1",
                      borderRadius: 3,
                      height: "100%",
                    }}
                  />
                  <Box sx={{ p: theme.spacing(2) }}>
                    <Skeleton variant="text" height={24} />
                    <Skeleton variant="text" height={16} />
                    <Skeleton
                      variant="rectangular"
                      height={theme.spacing(4)} // 32px
                      sx={{
                        mt: theme.spacing(2), // 16px
                        borderRadius: theme.spacing(1.25), // 10px
                      }}
                    />
                  </Box>
                </Card>
              ))
            : products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
        </Box>
      </Box>
    );
  }
);

CategorySection.displayName = "CategorySection";
